import {
  getCartDiscountDisplayRows,
  getNamedAppliedDiscountRows,
} from "../../shared/cart-discount-display.js";
import { getCartLineItemPricing } from "../../shared/cart-line-pricing.js";
import { CHECKOUT_TRACKING_KEYS } from "../../shared/checkout-tracking.js";
import {
  REWARD_TIER_TYPE,
  computeRewardBarState,
  getRewardTierBillLabel,
  isSpecificProductsRewardMode,
} from "../../shared/reward-bar.js";
import { getRewardBarMatchOptions } from "./reward-bar-runtime.js";

/** Line-level pricing used for MRP / compare-at bill rows. */
export function getCartLineBillPricing(item) {
  const pricing = getCartLineItemPricing(item);
  const { finalLine, originalLine, originalFromDiscounts, compareAtLine } =
    pricing;

  const mrpDiscountCents = Math.max(0, compareAtLine - finalLine);
  // Catalog MRP cut only (compare-at vs selling) — excludes line/order coupons.
  const catalogOnlyMrpCents = Math.max(
    0,
    compareAtLine > 0 ? compareAtLine - originalFromDiscounts : 0
  );

  return {
    finalLine,
    originalLine,
    compareAtLine,
    originalFromDiscounts,
    mrpDiscountCents,
    catalogOnlyMrpCents,
    savingsCents: Math.max(0, originalLine - finalLine),
  };
}

function getRewardTierIdFromItem(item) {
  const props = item?.properties;
  if (!props || typeof props !== "object") return "";
  return String(props[CHECKOUT_TRACKING_KEYS.REWARD_TIER_ID] || "");
}

/** Achieved free-gift product tiers (specific-products reward bar). */
function getAchievedFreeGiftTierIds(cart, rewardSetting) {
  if (!rewardSetting || !isSpecificProductsRewardMode(rewardSetting)) {
    return new Set();
  }
  // Must use same When match options as the drawer (collection/tag meta).
  const state = computeRewardBarState(
    cart,
    rewardSetting,
    undefined,
    getRewardBarMatchOptions()
  );
  const ids = new Set();
  for (const tier of state.tiers || []) {
    if (tier?.rewardType !== REWARD_TIER_TYPE.PRODUCT) continue;
    if (tier.isFreeGift === false) continue;
    const tierId = String(tier.id || "");
    if (
      !tierId ||
      !(
        state.achievedTierIds?.has?.(tier.id) ||
        state.achievedTierIds?.has?.(tierId)
      )
    ) {
      continue;
    }
    ids.add(tierId);
  }
  return ids;
}

function isGenericCouponLabel(label) {
  const text = String(label || "").trim();
  return !text || /^(coupon discount|discount|reward discount)$/i.test(text);
}

function rewardDiscountTiers(setting) {
  const tiers = Array.isArray(setting?.tiers) ? setting.tiers : [];
  return tiers.filter((tier) => tier?.rewardType === REWARD_TIER_TYPE.DISCOUNT);
}

/**
 * Prefer the merchant's discount message or description.
 * The stock footer label "Coupon discount" hides that, so a percentage tier
 * falls back to "5% discount".
 */
function resolveCouponLineLabel(row, options, sellingSubtotalCents) {
  const configured = String(options.couponDiscountLabel || "").trim();
  if (configured && !isGenericCouponLabel(configured)) return configured;

  const tiers = rewardDiscountTiers(options.rewardSection?.setting);
  const amountCents = Math.abs(Number(row?.amountCents) || 0);
  const base = Math.max(0, Number(sellingSubtotalCents) || 0);
  if (base > 0 && amountCents > 0) {
    const rate = (amountCents / base) * 100;
    const matched = tiers.find((tier) => {
      const value = Number(tier?.discount_value);
      return Number.isFinite(value) && Math.abs(value - rate) < 0.25;
    });
    if (matched) return getRewardTierBillLabel(matched);
  }

  const rowTitle = String(row?.title || "").trim();
  if (rowTitle && !isGenericCouponLabel(rowTitle)) return rowTitle;

  // Only an estimated reward-bar discount may use the tier message.
  // A real Shopify discount (Buy 1 Get 1, a code) keeps its own name.
  if (row?.source === "reward_estimate") {
    const withCopy = tiers.find(
      (tier) =>
        String(tier?.discount_message || "").trim() ||
        String(tier?.description || "").trim()
    );
    if (withCopy) return getRewardTierBillLabel(withCopy);
    if (tiers[0]) return getRewardTierBillLabel(tiers[0]);
  }

  return configured || options.discountLabel || "Discount";
}

/**
 * Build bill summary totals + rows for the cart drawer footer modal.
 * Total MRP is the sum of compare-at (fallback: selling). Discount on MRP is
 * MRP − selling. Coupon / payable math is unchanged.
 */
export function buildCartBillSummary(cart, options = {}) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const freeGiftTierIds = getAchievedFreeGiftTierIds(
    cart,
    options.rewardSection?.setting
  );
  let sellingSubtotalCents = 0;
  let mrpTotalCents = 0;
  let mrpDiscountCents = 0;
  let lineSavingsCents = 0;
  let pendingFreeGiftCents = 0;

  for (const item of items) {
    const pricing = getCartLineBillPricing(item);
    const tierId = getRewardTierIdFromItem(item);
    const isPendingFreeGift =
      Boolean(tierId) &&
      freeGiftTierIds.has(tierId) &&
      Number(pricing.finalLine) > 0;
    const sellingLine = isPendingFreeGift
      ? 0
      : Math.max(0, pricing.originalFromDiscounts);
    const mrpLine =
      pricing.compareAtLine > 0 ? pricing.compareAtLine : Math.max(0, pricing.originalFromDiscounts);

    if (isPendingFreeGift) {
      pendingFreeGiftCents += Math.max(0, pricing.finalLine);
      lineSavingsCents += Math.max(0, pricing.finalLine);
    } else {
      lineSavingsCents += pricing.savingsCents;
    }

    sellingSubtotalCents += sellingLine;
    mrpTotalCents += mrpLine;
    mrpDiscountCents += isPendingFreeGift
      ? Math.max(0, mrpLine)
      : pricing.catalogOnlyMrpCents;
  }

  // Catalog compare-at gap only — never fold line BXGY into this or it
  // double-counts with the named product discount row.
  mrpDiscountCents = Math.max(0, mrpDiscountCents);

  const { rows: orderDiscountRows, totalDiscountCents: orderDiscountCents } =
    getCartDiscountDisplayRows(cart, {
      ...options,
      rewardMatchOptions:
        options.rewardMatchOptions || getRewardBarMatchOptions(),
    });

  const cartTotalCents = Number(cart?.total_price) || 0;
  const cartOriginalTotalCents = Number(cart?.original_total_price) || 0;

  // Order-tier estimates + pending free-gift lines (Shopify Function may lag).
  const hasEstimatedOrderDiscount = orderDiscountRows.some(
    (row) => row?.source === "reward_estimate"
  );
  const payableTotalCents = Math.max(
    0,
    cartTotalCents -
      (hasEstimatedOrderDiscount ? orderDiscountCents : 0) -
      pendingFreeGiftCents
  );

  const compareAtTotalCents = Math.max(
    mrpTotalCents,
    sellingSubtotalCents,
    cartOriginalTotalCents,
    hasEstimatedOrderDiscount ? cartTotalCents : 0,
    payableTotalCents
  );

  const totalSavingsCents = Math.max(
    0,
    Math.max(lineSavingsCents, compareAtTotalCents - payableTotalCents)
  );
  const totalPriceCents = payableTotalCents;
  const subtotalCents = sellingSubtotalCents;

  const lines = [];

  const totalMrpLabelRaw = String(
    options.totalMrpLabel ||
      options.billSubtotalLabel ||
      options.subtotalLabel ||
      "Total MRP"
  ).trim();
  const totalMrpLabel =
    !totalMrpLabelRaw ||
    /^subtotal$/i.test(totalMrpLabelRaw) ||
    /^estimated total$/i.test(totalMrpLabelRaw)
      ? "Total MRP"
      : totalMrpLabelRaw;

  const deliveryLine = {
    key: "delivery",
    kind: options.detailLayout ? "meta" : "muted",
    label: options.deliveryFeeLabel || "Delivery fee",
    valueCents: 0,
    valueText: options.deliveryFeeText || "To be calculated",
  };

  lines.push({
    key: "total-mrp",
    kind: options.detailLayout ? "meta" : "neutral",
    label: totalMrpLabel,
    valueCents: compareAtTotalCents,
    valueText: null,
  });

  // Split Shopify names: product/line first, then order/cart-level.
  // Keeps "10% OFF" under the post-offer subtotal so it is not read as
  // 10% of Total MRP.
  const cartLevelTitles = new Set();
  for (const app of cart?.cart_level_discount_applications || []) {
    const key = String(app?.title || app?.description || "")
      .trim()
      .toLowerCase();
    if (key) cartLevelTitles.add(key);
  }
  const namedRows = getNamedAppliedDiscountRows(cart);
  const lineNamedRows = [];
  const orderNamedRows = [];
  for (const row of namedRows) {
    const key = String(row?.title || "").trim().toLowerCase();
    if (key && cartLevelTitles.has(key)) orderNamedRows.push(row);
    else lineNamedRows.push(row);
  }
  const namedAmountSet = new Set(
    namedRows
      .map((row) => Math.abs(Number(row.amountCents) || 0))
      .filter((cents) => cents > 0)
  );
  const hasCartLevelNamed = cartLevelTitles.size > 0;

  const appendDiscountLine = (row, label, index) => {
    const amount = Math.abs(Number(row?.amountCents) || 0);
    if (!amount || !label) return 0;
    lines.push({
      key: `order-${row.code || row.title || index}`,
      kind: "discount",
      label,
      valueCents: -amount,
      valueText: null,
      removable: Boolean(row.removable),
      code: row.code,
    });
    return amount;
  };

  if (options.detailLayout) {
    // Total MRP → Discount on MRP → product discounts → Subtotal →
    // order % / codes → delivery → grand total.
    if (mrpDiscountCents > 0) {
      lines.push({
        key: "mrp",
        kind: "discount",
        accent: true,
        label: options.mrpDiscountLabel || "Discount on MRP",
        valueCents: -mrpDiscountCents,
        valueText: null,
      });
    }

    let lineDiscountTotal = 0;
    if (options.showDiscounts !== false) {
      lineNamedRows.forEach((row, index) => {
        lineDiscountTotal += appendDiscountLine(
          row,
          String(row.title || row.code || "Discount").trim(),
          `line-${index}`
        );
      });
    }

    const detailSubtotalCents = Math.max(
      0,
      compareAtTotalCents - mrpDiscountCents - lineDiscountTotal
    );
    // Bridge row only when it differs from Total — same amount is redundant.
    if (detailSubtotalCents !== compareAtTotalCents) {
      lines.push({
        key: "selling-subtotal",
        kind: "subtotal",
        label: options.sellingSubtotalLabel || "Subtotal",
        valueCents: detailSubtotalCents,
        valueText: null,
      });
    }

    if (options.showDiscounts !== false) {
      orderNamedRows.forEach((row, index) => {
        appendDiscountLine(
          row,
          String(row.title || row.code || "Discount").trim(),
          `named-order-${index}`
        );
      });
      orderDiscountRows.forEach((row, index) => {
        const amount = Math.abs(Number(row.amountCents) || 0);
        if (!amount) return;
        if (row.source === "reward_estimate") {
          if (namedAmountSet.has(amount)) return;
          appendDiscountLine(
            row,
            resolveCouponLineLabel(row, options, detailSubtotalCents),
            `estimate-${index}`
          );
          return;
        }
        if (hasCartLevelNamed || namedAmountSet.has(amount)) return;
        appendDiscountLine(
          row,
          String(row.title || row.code || "Discount").trim(),
          `order-${index}`
        );
      });
    }

    lines.push(deliveryLine);
  } else {
    lines.push(deliveryLine);
    if (mrpDiscountCents > 0) {
      lines.push({
        key: "mrp",
        kind: "discount",
        label: options.mrpDiscountLabel || "Discount on MRP",
        valueCents: -mrpDiscountCents,
        valueText: null,
      });
    }
    if (options.showDiscounts !== false) {
      const seenTitles = new Set();
      const pushLegacy = (row, preferNamedLabel) => {
        if (!row?.amountCents) return;
        const label = preferNamedLabel
          ? String(row.title || row.code || "Discount").trim()
          : resolveCouponLineLabel(row, options, sellingSubtotalCents);
        const key = label.toLowerCase();
        if (!label || seenTitles.has(key)) return;
        seenTitles.add(key);
        appendDiscountLine(row, label, key);
      };
      for (const row of namedRows) pushLegacy(row, true);
      for (const row of orderDiscountRows) {
        const amount = Math.abs(Number(row.amountCents) || 0);
        if (!amount) continue;
        if (row.source === "reward_estimate") {
          if (namedAmountSet.has(amount)) continue;
          pushLegacy(row, false);
          continue;
        }
        if (hasCartLevelNamed || namedAmountSet.has(amount)) continue;
        pushLegacy(row, true);
      }
    }
  }

  lines.push({
    key: "grand-total",
    kind: "grand",
    label: options.grandTotalLabel || "Grand total",
    valueCents: payableTotalCents,
    valueText: null,
  });

  return {
    lines,
    orderDiscountRows,
    orderDiscountCents,
    mrpDiscountCents,
    subtotalCents,
    totalMrpCents: compareAtTotalCents,
    totalPriceCents,
    payableTotalCents,
    compareAtTotalCents,
    totalSavingsCents,
    showCompareAtTotal: compareAtTotalCents > payableTotalCents,
  };
}

export function formatSavingsBannerText(template, amountText) {
  const raw = String(
    template || "🎉 You're saving {amount} 🥳 !"
  );
  return raw.replace(/\{amount\}/gi, amountText || "");
}
